import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import {
  GSI_HISTORICAL_PHOTO_LAYERS,
  GSI_PHOTO_ATTRIBUTION,
  GSI_PHOTO_ATTRIBUTION_WITH_GRUS,
  NENDO_PHOTO_LAYER_NAME,
  NENDO_PHOTO_MAX_NATIVE_ZOOM,
  NENDO_PHOTO_MIN_ZOOM,
  buildGsiTileUrl,
  getNendoPhotoTileUrl,
} from "@/features/map/constants/gsiPhotoLayers";
import { MAP_MAX_ZOOM } from "@/features/map/components/LayerGroupControl";
import type { TileCoverageStatus } from "@/features/map/utils/tileCoverage";
import { GsiPhotoTileLayer } from "./GsiPhotoTileLayer";
import YearSelectableLayer from "./YearSelectableLayer";

interface Props {
  /** 全レイヤーの表示状態（LayerGroupControl と同じキー） */
  overlayVisibility: Record<string, boolean>;
  /** 透過率（0〜1）。将来の透過率設定 UI から渡す想定。 */
  opacity?: number;
  /**
   * 各レイヤーの表示範囲にデータがあるかの判定結果。
   * 年度別空中写真は年度に関係なく NENDO_PHOTO_LAYER_NAME で通知する。
   */
  onCoverageChange?: (
    layerName: string,
    status: TileCoverageStatus | null
  ) => void;
}

/**
 * 「年代別の写真」（国土地理院）のタイルレイヤー群。
 *
 * - 年度別空中写真（2007年以降）: `${NENDO_PHOTO_LAYER_NAME}_${year}` で選択中の 1 年度だけを描画
 * - 1928年頃〜1990年の年代別写真: ON になっているものをすべて描画（独立チェックボックス）
 *
 * いずれも全国最新写真と同じ zIndex 層に置き、ベースマップより上・地図サービス層より下に重ねる。
 */
export const DatedPhotoLayers: React.FC<Props> = ({
  overlayVisibility,
  opacity,
  onCoverageChange,
}) => {
  return (
    <>
      <YearSelectableLayer
        name={NENDO_PHOTO_LAYER_NAME}
        overlayVisibility={overlayVisibility}
        render={(year, _defaultVisible, visible) => (
          <GsiPhotoTileLayer
            key={year}
            visible={visible}
            className="nendo-photo-tile-layer"
            // 2023 年度の一部で GRUS 画像を使用しているため、全国最新写真と同じ出所明示を付ける
            attribution={GSI_PHOTO_ATTRIBUTION_WITH_GRUS}
            url={getNendoPhotoTileUrl(year)}
            minZoom={NENDO_PHOTO_MIN_ZOOM}
            maxNativeZoom={NENDO_PHOTO_MAX_NATIVE_ZOOM}
            maxZoom={MAP_MAX_ZOOM}
            zIndex={LAYER_Z_INDEX.SEAMLESS_PHOTO}
            opacity={opacity}
            onCoverageChange={
              onCoverageChange &&
              ((status) => {
                onCoverageChange(NENDO_PHOTO_LAYER_NAME, status);
              })
            }
          />
        )}
      />
      {/*
        年代別写真は同時に複数 ON にでき、zIndex が同じなので後から DOM に入った方が上に乗る。
        定義は新しい年代が先頭なので、そのまま描くと最も古い年代が一番上に来てしまう。
        古い年代から描いて、新しい年代が上に重なるようにする。
      */}
      {[...GSI_HISTORICAL_PHOTO_LAYERS].reverse().map((layer) => (
        <GsiPhotoTileLayer
          key={layer.name}
          visible={Boolean(overlayVisibility[layer.name])}
          className="historical-photo-tile-layer"
          attribution={GSI_PHOTO_ATTRIBUTION}
          url={buildGsiTileUrl(layer.tileId, layer.ext)}
          minZoom={layer.minZoom}
          maxNativeZoom={layer.maxNativeZoom}
          maxZoom={MAP_MAX_ZOOM}
          zIndex={LAYER_Z_INDEX.SEAMLESS_PHOTO}
          opacity={opacity}
          onCoverageChange={
            onCoverageChange &&
            ((status) => {
              onCoverageChange(layer.name, status);
            })
          }
        />
      ))}
    </>
  );
};
